import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet";

import DockDemo from "../components/dock/DockDemo";
import {
  FaSun,
  FaMoon,
  FaFlag,
  FaGoogle,
  FaLanguage,
  FaUniversity,
  FaMapMarkerAlt,
  FaDownload,
} from "react-icons/fa";
import CustomDrawer from "../components/CustomDrawer";
import MouseFollower from "../components/MouseFollower";
import { useCountAnimation } from "../hooks/useCountAnimation";
import Projects from "../components/Projects";
import Experiene from "../components/Experience";
import Leadership from "../components/Leadership";
import SkeletonLoader from "../components/SkeletonLoader";

const Hero = require("../assets/hero.jpeg");

const profileTags = [
  { label: "Indian", icon: FaFlag },
  { label: "GDGoC Organizer", icon: FaGoogle },
  { label: "Pune Based", icon: FaMapMarkerAlt },
  { label: "English", icon: FaLanguage },
  { label: "Hindi", icon: FaLanguage },
  { label: "Marathi", icon: FaLanguage },
  { label: "SPPU University", icon: FaUniversity },
];

function Home() {
  const { isDark, setIsDark } = useTheme();
  const experienceRef = useRef(null);
  const leadershipRef = useRef(null);
  const projectsRef = useRef(null);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [activeSection, setActiveSection] = useState("about");
  const [isLoading, setIsLoading] = useState(true);
  
  // Move hook calls to the top level, before any conditional returns
  const projectCount = useCountAnimation(15);
  const clientCount = useCountAnimation(2);
  const yearCount = useCountAnimation(1);

  useEffect(() => {
    // Simulate loading time (you can adjust the timeout)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500); // 2 seconds loading time

    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const primarySkills = [
    {
      name: "MongoDB",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
    },
    {
      name: "Express.js",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg",
    },
    {
      name: "React.js",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
    },
    {
      name: "Node.js",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
    },
  ];

  const secondarySkills = [
    {
      name: "JavaScript (ES6+)",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg",
    },
    {
      name: "Redux",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg",
    },
    {
      name: "Tailwind CSS",
      logo: "https://www.svgrepo.com/show/374118/tailwind.svg",
    },
    {
      name: "REST APIs",
      logo: "https://www.svgrepo.com/show/375531/api.svg",
    },
    {
      name: "HTML5",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg",
    },
    {
      name: "CSS3",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg",
    },
    {
      name: "Prompt Engineering",
      logo: "https://www.svgrepo.com/show/306500/openai.svg",
    },
    {
      name: "LLM Integration",
      logo: "https://www.svgrepo.com/show/306500/openai.svg",
    },
    {
      name: "Git",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg",
    },
    {
      name: "GitHub",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg",
    },
    {
      name: "C++",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg",
    },
    {
      name: "Python",
      logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
    },
  ];

  return (<>
        <Helmet>
            <title>Atharva Ralegankar Portfolio</title>
            <meta name="description" content="Proficient MERN Stack Developer with hands-on experience in creating scalable web applications using React.js, Node.js, MongoDB, and modern frontend tools." />
            <meta name="keywords" content="Best Portfolio Design, Portfolio Designs, Easy Portfolio, Bento Grid , bento grid examples, bento grid portfolio, Atharva Ralegankar, MERN Stack Developer, Full Stack Developer Pune, AI-Powered Applications, React.js Developer, Node.js Developer, MongoDB Expert, Tailwind CSS Designer, Google Developer Groups Organizer, SaaS Product Developer, AI Integration Specialist, Web Developer India, Gemini AI Projects" />
            <meta property="og:title" content="Atharva Ralegankar | Full Stack Web Developer" />
            <meta property="og:description" content="Proficient MERN Stack Developer with hands-on experience in creating scalable web applications." />
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Atharva Ralegankar | Full Stack Web Developer" />
            <meta name="twitter:description" content="Proficient MERN Stack Developer with hands-on experience in creating scalable web applications." />
            <link rel="canonical" href="https://your-website-url.com" />
          </Helmet>
    <AnimatePresence mode="wait">
      {isLoading ? (
        <SkeletonLoader key="skeleton" />
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <MouseFollower />
          <div
            className={`min-h-screen ${
              isDark ? "bg-dark-primary" : "bg-light-primary"
            }  flex justify-center items-center py-5  lg:py-0 px-8 md:px-5 lg:px-10`}
          >
            <div className="max-w-7xl mx-auto">
              {/* Theme Toggle Button */}
              <motion.button
                onClick={() => setIsDark(!isDark)}
                className={`fixed border-2 top-8 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center ${
                  isDark ? "bg-gray-900 text-white" : "bg-white text-gray-900"
                }`}
                whileHover={{ scale: 1.1, rotate: "1deg" }}
                whileTap={{ scale: 0.95, rotate: "-1deg" }}
              >
                {isDark ? (
                  <FaSun className="w-6 h-6" />
                ) : (
                  <FaMoon className="w-6 h-6" />
                )}
              </motion.button>

              {/* Main Grid Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
                {/* Profile Cards Row */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`col-span-1 md:col-span-6 lg:col-span-3 ${
                    isDark
                      ? "bg-gradient-to-r from-white to-gray-100"
                      : "bg-gradient-to-r from-white to-gray-200"
                  } rounded-3xl p-4 overflow-hidden shadow-lg`}
                >
                  <img
                    src={Hero}
                    alt="Profile"
                    className="w-full h-32 md:h-40 object-cover rounded-2xl"
                  />
                  <div className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {profileTags.map((tag) => (
                        <span
                          key={tag.label}
                          className="inline-flex  gap-0.5 px-1.5 py-0.5 bg-gray-100 
                   text-gray-800 rounded-full text-[10px] md:text-xs items-center"
                        >
                          <tag.icon className="w-2.5 h-2.5 md:w-3 md:h-3" />
                          <span className="hidden md:inline">{tag.label}</span>
                          <span className="pl-4 md:hidden">
                            {tag.label.length > 2
                              ? tag.label.substring(0, 2)
                              : tag.label}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                  <DockDemo />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`col-span-1 md:col-span-6 lg:col-span-3 ${
                    isDark
                      ? "bg-gradient-to-r from-gray-200 to-slate-300"
                      : "bg-gradient-to-r from-gray-200 to-gray-300"
                  } rounded-3xl p-6 shadow-lg relative overflow-hidden`}
                >
                  <div className="relative z-10">
                    <motion.h2
                      className={`font-display text-2xl font-bold mb-4 ${
                        isDark ? "text-light-RichBlack" : "text-light-RichBlack"
                      }`}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      Skills
                    </motion.h2>

                    {/* Primary Skills - MERN Stack */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      {primarySkills.map((skill, index) => (
                        <motion.div
                          key={skill.name}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.5,
                            delay: index * 0.1,
                          }}
                          className="flex flex-col items-center gap-2"
                        >
                          <div
                            className={`
        w-full aspect-square
        flex items-center justify-center
        ${isDark ? "bg-gray-100" : "bg-gray-100"}
        rounded-xl
        p-4
        transition-all duration-300
      `}
                          >
                            <img
                              src={skill.logo}
                              alt={skill.name}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span
                            className={`
        text-xs font-medium text-center
        ${isDark ? "text-gray-700" : "text-light-RichBlack"}
      `}
                          >
                            {skill.name.replace(".js", "")}
                          </span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Secondary Skills Carousel */}
                    <div className="relative">
                      <div className="overflow-hidden">
                        <motion.div
                          className="flex gap-4"
                          animate={{
                            x: [0, -1000],
                          }}
                          transition={{
                            duration: 20,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        >
                          {[...secondarySkills, ...secondarySkills].map(
                            (skill, index) => (
                              <motion.div
                                key={`${skill.name}-${index}`}
                                className="flex flex-col items-center gap-2 flex-shrink-0"
                              >
                                <div
                                  className={`
            w-16 aspect-square
            flex items-center justify-center
            ${isDark ? "bg-gray-100" : "bg-gray-100"}
            rounded-xl
            p-3
            transition-all duration-300
          `}
                                >
                                  <img
                                    src={skill.logo}
                                    alt={skill.name}
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                                <span
                                  className={`
            text-[10px] font-medium text-center
            ${isDark ? "text-light-RichBlack" : "text-gray-700"}
          `}
                                >
                                  {skill.name.replace(".js", "")}
                                </span>
                              </motion.div>
                            )
                          )}
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Hero Section */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`col-span-1 md:col-span-6 lg:col-span-6 row-span-2 ${
                    isDark
                      ? "bg-gradient-to-r from-yellow-300 to-yellow-400"
                      : "bg-gradient-to-r from-yellow-300 to-yellow-400"
                  } rounded-3xl p-6 md:p-8 shadow-lg relative overflow-hidden`}
                >
                  {/* Background Logos Overlay */}
                  <div className="absolute inset-0 overflow-hidden opacity-20">
                    {primarySkills.map((skill, i) => {
                      // Calculate grid positions
                      const positions = [
                        { x: "25%", y: "25%" },
                        { x: "25%", y: "75%" },
                        { x: "75%", y: "5%" },
                        { x: "75%", y: "75%" },
                      ];

                      return (
                        <motion.img
                          key={i}
                          src={skill.logo}
                          alt="skill"
                          className="absolute w-40 h-40 opacity-60"
                          style={{
                            left: positions[i].x,
                            top: positions[i].y,
                            filter: "blur(1px)",
                            transform: "translate(-50%, -50%)",
                          }}
                          animate={{
                            y: [0, -15, 0],
                            x: [0, 10, 0],
                            rotate: [0, i % 2 ? 10 : -10, 0],
                            scale: [1, 1.1, 1],
                          }}
                          transition={{
                            duration: 6,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: i * 1.5,
                            times: [0, 0.5, 1],
                          }}
                        />
                      );
                    })}
                  </div>

                  {/* Content */}
                  <div className="h-full flex flex-col relative z-10">
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-text mb-2">
                      Atharva Ralegankar
                    </h1>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-text/80 mb-4">
                      Full Stack Web Developer
                    </h2>
                    <p className="font-body text-text/70 mb-6">
                      Proficient MERN Stack Developer with hands-on experience
                      in creating scalable web applications using React.js,
                      Node.js, MongoDB, and modern frontend tools like Tailwind
                      CSS and Redux. Selected by Google as an Organizer for the
                      Google Developer Groups On Campus, showcasing leadership
                      and collaboration skills.
                    </p>
                    <motion.button
                      whileHover={{ y: -2 }}
                      className="px-4 py-2 md:px-6 md:py-3 bg-black text-white rounded-full w-fit flex items-center gap-5"
                    >
                      <FaDownload className="w-4 h-4" />
                      Resume
                    </motion.button>
                  </div>
                </motion.div>

                {/* Experience Stats Cards */}
                <div className="col-span-1 md:col-span-6 flex flex-col md:flex-row gap-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => scrollToSection(experienceRef)}
                    className={`flex-1 cursor-pointer ${
                      isDark
                        ? "bg-dark-DesaturatedTeal text-dark-textDesaturatedTeal"
                        : "bg-light-DesaturatedTeal text-light-textDesaturatedTeal"
                    } rounded-3xl p-4 md:p-6 shadow-lg`}
                  >
                    <div className="text-center">
                      <motion.h3 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">
                        {projectCount}+
                      </motion.h3>
                      <p className="font-display text-sm mt-1 opacity-70">
                        Projects
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => scrollToSection(experienceRef)}
                    className={`flex-1 cursor-pointer ${
                      isDark
                        ? "bg-dark-DesaturatedTeal text-dark-textDesaturatedTeal"
                        : "bg-light-DesaturatedTeal text-light-textDesaturatedTeal"
                    } rounded-3xl p-4 md:p-6 shadow-lg`}
                  >
                    <div className="text-center">
                      <motion.h3 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">
                        {clientCount}
                      </motion.h3>
                      <p className="font-display text-sm mt-1 opacity-70">
                        Clients
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    onClick={() => scrollToSection(experienceRef)}
                    className={`flex-1 cursor-pointer ${
                      isDark
                        ? "bg-dark-DesaturatedTeal text-dark-textDesaturatedTeal"
                        : "bg-light-DesaturatedTeal text-light-textDesaturatedTeal"
                    } rounded-3xl p-4 md:p-6 shadow-lg`}
                  >
                    <div className="text-center">
                      <motion.h3 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">
                        {yearCount}
                      </motion.h3>
                      <p className="font-display text-sm mt-1 opacity-70">
                        Years
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Contact Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`col-span-1 md:col-span-6 lg:col-span-6 ${
                    isDark
                      ? "bg-gradient-to-r from-yellow-300 to-yellow-400"
                      : "bg-gradient-to-r from-yellow-300 to-yellow-400"
                  } rounded-3xl p-6 md:p-8 relative overflow-hidden shadow-lg`}
                >
                  <div className="flex flex-col h-full justify-center items-center text-center">
                    <p className="font-body text-lg">Lets Work Together</p>
                    <a
                      href="mailto:ralegankaratharva@gmail.com"
                      className="font-display text-xl md:text-xl lg:text-2xl font-bold"
                    >
                      ralegankaratharva@gmail.com
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{
                    scale: 1.02,
                    transition: { duration: 0.3 },
                  }}
                  onClick={() => window.open("https://yourblogurl.com", "_blank")}
                  className={`col-span-1 md:col-span-6 lg:col-span-6 cursor-pointer rounded-3xl p-8 relative overflow-hidden group ${
                    isDark
                      ? "bg-dark-Chestnut text-dark-textChestnut"
                      : "bg-light-Chestnut text-light-textChestnut"
                  }`}
                >
                  {/* Animated Background Orbs */}
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute rounded-full opacity-30 blur-3xl"
                      style={{
                        width: `${150 + i * 50}px`,
                        height: `${150 + i * 50}px`,
                        background: isDark
                          ? "radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)"
                          : "radial-gradient(circle, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.05) 100%)",
                        left: `${20 + i * 30}%`,
                        top: `${20 + i * 20}%`,
                      }}
                      animate={{
                        scale: [1, 1.2, 1],
                        x: [0, 30, 0],
                        y: [0, -30, 0],
                      }}
                      transition={{
                        duration: 8 + i * 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  ))}

                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex-1">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex items-center gap-4 "
                      >
                        <div className="w-1 h-12 rounded-full bg-current opacity-50" />
                        <div>
                          <motion.h3
                            className="font-display text-xl md:text-2xl lg:text-3xl font-bold "
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                          >
                            Atharva's Blog Space
                          </motion.h3>
                          <motion.p
                            className="text-sm md:text-lg lg:text-xl opacity-80"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                          >
                            Click to explore my development journey & tech insights
                          </motion.p>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>

                {/* Leadership Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setActiveDrawer("leadership")}
                  className={`col-span-1 md:col-span-4 lg:col-span-4 cursor-pointer p-6 md:p-8 rounded-3xl shadow-lg ${
                    isDark
                      ? "bg-dark-MossGreen text-dark-textMossGreen"
                      : "bg-light-MossGreen text-light-textMossGreen"
                  } flex items-center justify-center gap-4 relative overflow-hidden`}
                >
                  <motion.div
                    animate={{
                      opacity: [0.7, 0.3, 0.7],
                      scale: [1, 1.5, 1],
                      rotate: [0, 10, -10, 0],
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute w-40 h-40  rounded-full blur-2xl z-0"
                  />
                  <div className="relative z-10 text-center">
                    <span className="font-body text-sm tracking-tigher  flex justify-center">
                      click to open me
                    </span>
                    <h2 className="text-2xl  md:text-3xl font-bold  font-display flex justify-center">
                      Leadership
                    </h2>
                  </div>
                </motion.div>

                {/* Experience Card */}

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setActiveDrawer("experience")}
                  className={`col-span-1 md:col-span-2 lg:col-span-4 cursor-pointer ${
                    isDark
                      ? "bg-dark-RichBlack text-dark-textRichBlack"
                      : "bg-light-RichBlack text-light-textRichBlack"
                  } p-6 md:p-8 rounded-3xl shadow-lg flex items-center justify-center gap-4 relative overflow-hidden`}
                >
                  <div className="relative z-10 text-center">
                    <span className="font-body text-sm tracking-tigher  flex justify-center">
                      click to open me
                    </span>
                    <span className="text-2xl  md:text-3xl font-bold  font-display flex justify-center">
                      Experience
                    </span>
                  </div>
                </motion.div>

                {/* Projects Card */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setActiveDrawer("projects")}
                  className={`col-span-1 md:col-span-6 lg:col-span-4 cursor-pointer p-6 md:p-8 rounded-3xl shadow-lg ${
                    isDark
                      ? "bg-dark-Maroon text-white"
                      : "bg-light-Maroon text-light-textMarron"
                  } flex items-center justify-center gap-4 relative overflow-hidden `}
                >
                  <motion.div
                    animate={{
                      opacity: [0.7, 0.3, 0.7],
                      scale: [1, 1.5, 1],
                      rotate: [0, -10, 10, 0],
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute w-56 h-56 bg-red-800/40 rounded-full blur-2xl z-0"
                  />
                  <div className="relative z-10 w-full text-left">
                    <span className="font-body text-sm tracking-tigher  flex justify-center">
                      click to open me
                    </span>
                    <h2 className="font-display text-3xl md:text-3xl font-bold -tracking-tighter  flex justify-center">
                      Projects
                    </h2>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Drawers */}
          <CustomDrawer
            isOpen={activeDrawer === "leadership"}
            onClose={() => setActiveDrawer(null)}
            title="Leadership & Activities"
          >
            <Leadership />
          </CustomDrawer>

          <CustomDrawer
            isOpen={activeDrawer === "projects"}
            onClose={() => setActiveDrawer(null)}
            title="Featured Projects"
          >
            <Projects />
          </CustomDrawer>

          <CustomDrawer
            isOpen={activeDrawer === "experience"}
            onClose={() => setActiveDrawer(null)}
            title="Professional Experience"
          >
            <Experiene />
          </CustomDrawer>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}

export default Home;
